import React, { useState, useEffect } from 'react';
import { Wrench } from 'lucide-react';

const formatElapsed = (startIso) => {
  if (!startIso) return null;
  const start = new Date(startIso).getTime();
  if (Number.isNaN(start)) return null;
  let secs = Math.max(0, Math.floor((Date.now() - start) / 1000));
  const days = Math.floor(secs / 86400);
  secs -= days * 86400;
  const hours = Math.floor(secs / 3600);
  secs -= hours * 3600;
  const mins = Math.floor(secs / 60);
  secs -= mins * 60;
  const parts = [];
  if (days) parts.push(`${days}d`);
  if (days || hours) parts.push(`${hours}h`);
  parts.push(`${mins}m`);
  parts.push(`${secs}s`);
  return parts.join(' ');
};

export const useElapsed = (startIso) => {
  const [elapsed, setElapsed] = useState(() => formatElapsed(startIso));
  useEffect(() => {
    setElapsed(formatElapsed(startIso));
    if (!startIso) return undefined;
    const id = setInterval(() => setElapsed(formatElapsed(startIso)), 1000);
    return () => clearInterval(id);
  }, [startIso]);
  return elapsed;
};

// Full-panel maintenance notice shown to all users when a test is offline.
export const MaintenanceNotice = ({ testName, message, startedAt }) => {
  const elapsed = useElapsed(startedAt);
  return (
    <div
      className="max-w-md mx-auto bg-white border border-amber-200 rounded-2xl shadow-lg overflow-hidden"
      data-testid="maintenance-notice"
    >
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-5 flex items-center gap-3">
        <div className="w-11 h-11 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
          <Wrench className="w-6 h-6 text-white animate-pulse" />
        </div>
        <div>
          <p className="text-white/80 text-xs uppercase tracking-wide font-semibold">Temporarily Offline</p>
          <h2 className="text-white font-heading text-xl font-bold leading-tight">
            {testName || 'This test'} is under maintenance
          </h2>
        </div>
      </div>
      <div className="px-6 py-6 text-center">
        <p className="text-[#475569] text-base mb-6" data-testid="maintenance-message">
          {message || 'This test is temporarily under maintenance. It will be back soon.'}
        </p>
        {elapsed && (
          <div className="inline-flex flex-col items-center gap-1 bg-amber-50 border border-amber-200 rounded-xl px-6 py-4">
            <span className="text-xs uppercase tracking-wide text-amber-700 font-semibold">
              Under maintenance for
            </span>
            <span
              className="font-mono text-2xl font-bold text-amber-900 tabular-nums"
              data-testid="maintenance-timer"
            >
              {elapsed}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// Compact inline banner (used on the test detail page / admin preview).
export const MaintenanceBanner = ({ message, startedAt, adminView = false }) => {
  const elapsed = useElapsed(startedAt);
  return (
    <div
      className="flex flex-col sm:flex-row sm:items-center gap-3 bg-amber-50 border border-amber-300 rounded-lg px-4 py-3"
      data-testid="maintenance-banner"
    >
      <div className="flex items-center gap-2 text-amber-800 font-semibold">
        <Wrench className="w-4 h-4" />
        {adminView ? 'Under maintenance (visible to all users)' : 'Under maintenance'}
      </div>
      <p className="text-amber-800/90 text-sm flex-1">
        {message || 'This test is temporarily under maintenance. It will be back soon.'}
      </p>
      {elapsed && (
        <span className="text-amber-900 text-sm font-mono font-semibold tabular-nums whitespace-nowrap">
          {elapsed}
        </span>
      )}
    </div>
  );
};
